import clsx from "clsx"
import { type ForwardedRef, type KeyboardEvent, type MouseEvent, forwardRef, useState } from "react"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { Divider, Icon } from "@atoms/index"

import { ListItemContext } from "./ListItem.context"
import styles from "./ListItem.module.scss"
import { ListItemAccordionProps, ListItemProps, ListItemRowRendererProps } from "./ListItem.types"
import ListItemActions from "./ListItemActions/ListItemActions"
import ListItemAvatarAndContent from "./ListItemAvatarAndContent/ListItemAvatarAndContent"
import ListItemBottom from "./ListItemBottom/ListItemBottom"
import ListItemCaption from "./ListItemCaption/ListItemCaption"
import ListItemContent from "./ListItemContent/ListItemContent"
import ListItemLabel from "./ListItemLabel/ListItemLabel"

function ListItemRow(props: ListItemRowRendererProps, ref: ForwardedRef<HTMLDivElement>) {
    const {
        children,
        className,
        isLoading = false,
        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isClickable = false,
        isDraggable = false,
        isDragging = false,
        isAccordion = false,
        isOpen = false,
        onClick,
        ...rest
    } = props

    const isInteractive = isClickable && !isDisabled

    function handleClick(event: MouseEvent<HTMLDivElement>) {
        if (isInteractive) {
            onClick?.(event)
        }
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (isInteractive) {
            ifSpaceOrEnter(event, () => {
                onClick?.(event)
            })
        }
    }

    const rootClassName = clsx(
        styles.base,
        {
            [styles.interactive]: isClickable || isDraggable,
            [styles.draggable]: isDraggable,
            [styles.clickable]: isClickable,
            [styles.isFocused]: _isFocused,
            [styles.isHovered]: _isHovered,
            [styles.isPressed]: isPressed,
            [styles.isDisabled]: isDisabled,
            [styles.isDragging]: isDragging,
        },
        className,
    )

    return (
        <ListItemContext.Provider value={{ isLoading, isDragging }}>
            <div
                ref={ref}
                className={rootClassName}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? (isDisabled ? -1 : 0) : undefined}
                aria-disabled={isClickable ? isDisabled : undefined}
                onClick={isInteractive ? handleClick : undefined}
                onKeyDown={isInteractive ? handleKeyDown : undefined}
                {...rest}
            >
                {isAccordion && (
                    <div className={clsx(styles.caret, { [styles.caretOpen]: isOpen })} aria-hidden="true">
                        <Icon
                            icon={faIcon({ name: "caret-right", style: "solid" })}
                            size={14}
                            container="fixedHeight"
                        />
                    </div>
                )}
                {isDraggable && (
                    <div className={styles.dragHandle} aria-hidden="true">
                        <Icon
                            icon={faIcon({
                                name: isDragging ? "arrows-up-down" : "grip-dots-vertical",
                                style: "regular",
                            })}
                            size={14}
                            container="fixedHeight"
                            className={clsx(styles.dragHandleIcon, { [styles.dragHandleIconDragging]: isDragging })}
                        />
                    </div>
                )}
                {children}
            </div>
        </ListItemContext.Provider>
    )
}

const ListItemRowWithRef = forwardRef(ListItemRow)

function ListItemAccordion(props: ListItemAccordionProps, ref: ForwardedRef<HTMLDivElement>) {
    const { className, accordionBody, isOpen: controlledIsOpen, onOpenChange, ...rowProps } = props

    const isControlled = controlledIsOpen !== undefined
    const [internalIsOpen, setInternalIsOpen] = useState(false)
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen

    function handleToggle() {
        if (isControlled) {
            onOpenChange?.(!isOpen)
        } else {
            setInternalIsOpen(!isOpen)
        }
    }

    return (
        <div ref={ref} className={clsx(styles.accordion, { [styles.accordionOpen]: isOpen }, className)}>
            <ListItemRowWithRef
                {...rowProps}
                isOpen={isOpen}
                isClickable={true}
                onClick={handleToggle}
                aria-expanded={isOpen}
            />

            {isOpen && (
                <>
                    <Divider />
                    <div className={styles.accordionPanel}>{accordionBody}</div>
                </>
            )}
        </div>
    )
}

const ListItemAccordionWithRef = forwardRef(ListItemAccordion)

function ListItemRoot(props: ListItemProps, ref: ForwardedRef<HTMLDivElement>) {
    if (props.isAccordion) {
        return <ListItemAccordionWithRef ref={ref} {...props} />
    } else {
        return <ListItemRowWithRef ref={ref} {...props} />
    }
}

const ListItem = Object.assign(forwardRef(ListItemRoot), {
    Caption: ListItemCaption,
    Label: ListItemLabel,
    Actions: ListItemActions,
    Bottom: ListItemBottom,
    AvatarAndContent: ListItemAvatarAndContent,
    Content: ListItemContent,
})

export default ListItem
